<template>
  <CCardBody>
    <alert-section
      :successAlertMessage="successAlertMessage"
      :dismissSecs="dismissSecs"
      :dismissSuccessAlert="dismissSuccessAlert"
      :errorAlertMessage="errorAlertMessage"
      :showErrorAlert="showErrorAlert"
    />

    <CCard class="filters" accent-color="warning">
      <CCardHeader
        role="button"
        class="text-warning shadow-none card-header"
        @click="resetFilters"
      >
        <strong class="m-0"><CIcon :name="`cil-filter${$store.state.filterShow ? '-x' : ''}`" /> Filters</strong>
        <div class="card-header-actions">
          <CIcon :name="`cil-chevron-${$store.state.filterShow ? 'bottom' : 'top'}`"/>
        </div>
      </CCardHeader>

      <CCollapse :show="$store.state.filterShow">
        <CCardBody class="p-2">
          <div class="d-flex flex-md-row flex-column" style="gap: 0.5rem;">
            <div class="flex-grow-1">
              <v-select
                class="v-select-filter"
                placeholder="Select company.."
                v-model="selectedCompany"
                :options="allCompanies"
                @input="companyFilter"
              />
            </div>

            <CInput
              class="mb-0"
              type="date"
              v-model="dateFrom"
              @input="getAllItems"
            >
              <template #prepend-content>From</template>
            </CInput>

            <CInput
              class="mb-0"
              type="date"
              v-model="dateTo"
              @input="getAllItems"
            >
              <template #prepend-content>To</template>
            </CInput>
          </div>
        </CCardBody>
      </CCollapse>
    </CCard>

    <CDataTable
      striped
      hover
      :items="loadedItems"
      :fields="fields"
      :sorter="{ external: true, resetable: true }"
      :itemsPerPageSelect="{
        external: true,
        values: [5, 15, 25, 50, 100, 250, 500],
      }"
      :items-per-page.sync="itemsPerPage"
      :loading="loading"
      :noItemsView="{
        noResults: 'No filtering results are available!',
        noItems: 'No MenutePay internal settlements found!',
      }"
      @update:sorter-value="sorterValue"
      @pagination-change="paginationChange"
    >
      <template #id="{item}">
        <td>#{{ item.id }}</td>
      </template>

      <template #company="{item}">
        <td>
          #{{ item.company ? item.company.id : item.company_id }}
          <strong>{{ item.company ? item.company.name : null }}</strong>
        </td>
      </template>

      <template #period="{item}">
        <td>
          {{ formatDate(item.period_start) }} - {{ formatDate(item.period_end) }}
        </td>
      </template>

      <template #usage_total="{item}">
        <td>
          <strong>{{ item.usage_total | toCurrency('remove_thousand_separator') }}</strong>
        </td>
      </template>

      <template #status="{item}">
        <td class="text-center">
          <CBadge color="info">{{ item.status }}</CBadge>
        </td>
      </template>

      <template #created_at="{item}">
        <td>{{ formatDateTime(item.created_at) }}</td>
      </template>

      <template #actions="{item}">
        <td class="py-2 text-center">
          <CButton
            size="sm"
            color="primary"
            shape="pill"
            v-c-tooltip="{ html: true, content: 'Download payroll report' }"
            @click="downloadSettlement(item)"
          >
            <CIcon name="cil-cloud-download" />
          </CButton>
        </td>
      </template>
    </CDataTable>

    <CCard class="actions sticky-bottom">
      <CCardBody class="p-2">
        <div class="d-flex flex-wrap align-items-center" style="gap: 0.75rem">
          <div>
            <h5 class="mt-1">Total: <span class="d-inline count bg-primary pb-1">{{ total }}</span></h5>
          </div>

          <div v-if="isSuperAdmin" class="action">
            <CButton
              size="sm"
              color="success"
              @click="makeSettlementModal = true"
            >
              <CIcon name="cil-send" /> Create Settlements
            </CButton>
          </div>

          <div v-if="pages > 1" class="ml-auto">
            <CPagination
              align="center"
              :dots='false'
              :pages="pages"
              :active-page.sync="activePage"
            />
          </div>
        </div>
      </CCardBody>
    </CCard>

    <CModal v-if="isSuperAdmin" class="make-settlements" title="Create Internal Settlements" size="sm" color="success" centered :show.sync="makeSettlementModal">
      <CInput
        type="month"
        v-model="selectedMonth"
        :max="prevMonth"
      />

      <template #footer class="p-1">
        <CButton @click="makeSettlementModal = false" color="link">Cancel</CButton>
        <CButton @click="makeSettlements" :disabled="!selectedMonth" size="sm" color="success">
          <CIcon name="cil-send" /> Create
        </CButton>
      </template>
    </CModal>

    <mc-spinner :opacity="0.8" v-show="submitted" :mtop="(32 + mcSpinnerMarginTop) + 'px'" />
  </CCardBody>
</template>

<script>
export default {
  name: "BackendTable",
  data() {
    return {
      loadedItems: [],
      fields: [
        { key: "actions", label: "Actions", filter: false, sorter: false, _style: "min-width: 80px; width: 80px; text-align: center;" },
        { key: "id", label: "ID", filter: false, _style: "min-width: 50px; width: 50px;" },
        { key: "billing_entity_code", label: "Entity", filter: false, _style: "min-width: 70px; width: 80px;" },
        { key: "company", label: "Company", filter: false, sorter: false, _style: "min-width: 220px;" },
        { key: "period", label: "Period", filter: false, sorter: false, _style: "min-width: 190px;" },
        { key: "usage_total", label: "Usage", filter: false, _style: "min-width: 110px; width: 130px;" },
        { key: "transaction_count", label: "Transactions", filter: false, _style: "min-width: 110px; width: 130px;" },
        { key: "status", label: "Status", filter: false, _style: "min-width: 90px; width: 110px; text-align: center;" },
        { key: "created_at", label: "Created", filter: false, _style: "min-width: 160px; width: 170px;" },
      ],

      activePage: 1,
      pages: 1,
      total: 0,
      items_per_page: null,
      orderBy: null,
      asc: null,

      loading: false,
      submitted: false,

      successAlertMessage: {},
      dismissSecs: 10,
      dismissSuccessAlert: 0,
      errorAlertMessage: {},
      showErrorAlert: false,

      allCompanies: [],
      selectedCompany: null,
      dateFrom: null,
      dateTo: null,

      mcSpinnerMarginTop: 0,

      makeSettlementModal: false,
      selectedMonth: null,
    };
  },

  async mounted() {
    this.activePage = this.queryPage;
    this.selectedMonth = this.prevMonth;
    await this.getAllCompanies();
    await this.getAllItems();

    window.onscroll = () => {
      this.mcSpinnerMarginTop = document.documentElement.scrollTop;
    }
  },

  watch: {
    reloadParams() {
      if(this.queryPage != this.activePage) {
        this.$router.push({ name: this.$route.name, query: { page: this.activePage } });
      }
      this.onTableChange();
    },
  },

  computed: {
    itemsPerPage: {
      get: function () { return this.items_per_page ? this.items_per_page : parseInt(process.env.VUE_APP_ITEMS_PER_PAGE) },
      set: function (newValue) { this.items_per_page = newValue }
    },
    reloadParams() { return [this.activePage] },
    queryPage() { return parseInt(this.$route.query.page) || 1 },
    prevMonth() { return this.moment(new Date()).subtract(1,'months').format('YYYY-MM') },
    isSuperAdmin() { return this.$store.getters["auth/isSuperAdmin"] },
  },

  methods: {
    async getAllItems() {
      this.loading = true;

      var url = new URL(this.$backend.MENUTEPAY.INTERNAL_SETTLEMENTS.GET_ALL),
        params = {
          page: this.activePage,
          itemsPerPage: this.itemsPerPage,
          company: (this.selectedCompany && this.selectedCompany.value) ? this.selectedCompany.value : null,
          date_from: this.dateFrom,
          date_to: this.dateTo,
          orderBy: this.orderBy,
          asc: this.asc,
        };

      Object.keys(params).forEach((key) => {
        if (
          typeof params[key] !== "undefined" &&
          params[key] !== null &&
          params[key] !== "" &&
          params[key].length !== 0
        )
          url.searchParams.append(key, params[key]);
      });

      await this.$axios.get(url)
        .then((response) => response.data)
        .then((items) => {
          if(items.current_page > items.last_page)
            this.activePage = 1;
          else {
            this.loadedItems = items.data;
            this.activePage = items.current_page;
            this.pages = items.last_page;
            this.total = items.total;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async getAllCompanies() {
      await this.$axios
        .get(this.$backend.MENUTEPAY.COMPANIES.GET_LIST)
        .then((response) => {
          this.allCompanies = response.data;
        })
        .catch(() => { this.allCompanies = [] })
    },

    async downloadSettlement(item) {
      this.submitted = true;

      await this.$axios
        .get(this.$backend.MENUTEPAY.INTERNAL_SETTLEMENTS.DOWNLOAD.replace("{id}", item.id), { responseType: 'blob' })
        .then(({ data }) => {
          const period = this.moment(item.period_start).format('YYYY_MM');
          const fileName = `MenutePay_Internal_Settlement_${item.company_id}_${item.billing_entity_code}_${period}.csv`;
          const url = window.URL.createObjectURL(new Blob([data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .finally(() => this.submitted = false);
    },

    async makeSettlements() {
      if (!this.isSuperAdmin) return;

      this.makeSettlementModal = false;
      this.submitted = true;

      const payload = {
        date: this.selectedMonth,
      };

      await this.$axios
        .post(this.$backend.MENUTEPAY.INTERNAL_SETTLEMENTS.MAKE, payload)
        .then((response) => {
          this.getAllItems();
          this.showErrorAlert = false;
          this.successAlertMessage = { itemName: 'Success:', message: response.data.message };
          this.dismissSuccessAlert = this.dismissSecs;
        })
        .catch((error) => {
          this.errorAlertMessage = error.response.data;
          this.showErrorAlert = true;
        })
        .finally(() => {
          window.scrollTo(0,0);
          this.submitted = false;
          this.selectedMonth = this.prevMonth;
        });
    },

    onTableChange() {
      this.getAllItems();
    },
    paginationChange(value) {
      this.itemsPerPage = value;
      this.getAllItems();
    },
    sorterValue(item) {
      this.orderBy = item.column;
      this.asc = item.column ? item.asc : null;
      this.getAllItems();
    },
    companyFilter() {
      this.getAllItems();
    },
    resetFilters() {
      if (this.$store.state.filterShow) {
        this.selectedCompany = null;
        this.dateFrom = null;
        this.dateTo = null;
        this.getAllItems();
      }
      this.$store.commit('toggle', 'filterShow');
    },
    formatDate(value) {
      return value ? this.moment(value).format('YYYY-MM-DD') : '';
    },
    formatDateTime(value) {
      return value ? this.moment(value).format('YYYY-MM-DD HH:mm') : '';
    },
  },
};
</script>

<style>
.table th, .table td {
  padding: 0.5rem 0.25rem;
  vertical-align: middle;
}

.make-settlements .modal-header {
  padding: 0.75rem !important;
}
.make-settlements .modal-footer {
  padding: 0.25rem !important;
}
</style>

<style scoped>
.actions .action {
  border-right: 1px solid #d8dbe0 !important;
  padding-right: 0.75rem !important;
}
</style>
